import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Home, Sparkles, Activity, Clock, ChevronRight, LogOut } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TOOL_BY_KEY, CATEGORIES } from './toolRegistry';
import { buildOrderedGroups } from './sidebarShared';
import SystemHealthBadge from '../SystemHealthBadge';

// One clean, Apple-style sidebar. No layout options — a single, focused design.
// Styling leans on the app's semantic theme tokens so it adapts to light / dark
// automatically. Selected row = solid accent fill with a white glyph + label.
// Rows carry a macOS-System-Settings colour icon tile for a true Apple feel.

const CAT_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
const catColor = (toolKey) => CAT_BY_ID[TOOL_BY_KEY[toolKey]?.category]?.dot || '#8E8E93';
const NAV_COLOR = { home: '#0071E3', myweek: '#7C3AED', 'system-diagnostics': '#34C759', 'my-activity': '#FF9500' };
const OPEN_GROUPS_KEY = 'derwent.sidebar.openGroups';

const IconTile = ({ color, icon: Icon, active }) => (
  <span
    className={cn('shrink-0 w-[26px] h-[26px] rounded-[7px] flex items-center justify-center', active && 'bg-white/25')}
    style={active ? undefined : { backgroundColor: color }}
  >
    {Icon && <Icon className="w-[15px] h-[15px] text-white" strokeWidth={2} />}
  </span>
);

const Item = ({ icon: Icon, label, active, onClick, testid, sub, color }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn(
      'group w-full flex items-center gap-2.5 rounded-[10px] transition-colors duration-150',
      sub ? 'h-8 px-2.5 text-[13.5px]' : 'h-10 px-2 text-[14px]',
      active
        ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold'
        : 'text-foreground/85 hover:bg-foreground/[0.055] font-medium',
    )}
  >
    {color && !sub ? (
      <IconTile color={color} icon={Icon} active={active} />
    ) : (
      Icon && (
        <Icon
          className={cn('shrink-0', sub ? 'w-[16px] h-[16px]' : 'w-[19px] h-[19px]', active ? 'text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground group-hover:text-foreground')}
          strokeWidth={active ? 2.1 : 1.85}
        />
      )
    )}
    <span className="truncate flex-1 text-left">{label}</span>
  </button>
);

const SectionHeader = ({ children }) => (
  <div className="px-2 mt-6 mb-1.5">
    <span className="text-[12px] font-semibold text-muted-foreground/80">{children}</span>
  </div>
);

const Group = ({ cat, items, open, onToggle, activeKey, onLaunch }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`sidebar-group-${cat.id}`}
      className="group w-full flex items-center gap-2.5 h-10 px-2 rounded-[10px] text-[14px] font-medium text-foreground/85 hover:bg-foreground/[0.055] transition-colors"
    >
      <IconTile color={cat.dot} icon={cat.icon} />
      <span className="flex-1 text-left truncate">{cat.label}</span>
      <span className="text-[12px] tabular-nums text-muted-foreground/60 tracking-tight">{items.length}</span>
      <ChevronRight className={cn('w-3.5 h-3.5 shrink-0 text-muted-foreground/70 transition-transform duration-200', open && 'rotate-90')} strokeWidth={2.4} />
    </button>
    {open && (
      <div className="my-0.5 ml-[26px] pl-3 border-l border-border/70 space-y-0.5">
        {items.map((t) => (
          <Item key={t.key} icon={t.icon} label={t.title} sub active={activeKey === t.key} onClick={() => onLaunch(t)} testid={`sidebar-tool-${t.key}`} />
        ))}
      </div>
    )}
  </div>
);

const UserBlock = ({ user, onLogout }) => {
  const initials = (user?.name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const role = (user?.role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="p-3 border-t border-border/60">
      <div className="flex items-center gap-3 rounded-[12px] px-2 py-1.5 hover:bg-foreground/[0.045] transition-colors" data-testid="sidebar-user">
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt={user?.name || 'User'} className="w-9 h-9 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center justify-center text-[13px] font-semibold shrink-0">{initials}</div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-foreground truncate leading-tight">{user?.name}</p>
          <p className="text-[11.5px] text-muted-foreground truncate leading-tight mt-0.5">{role}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="sidebar-logout"
          className="w-8 h-8 inline-flex items-center justify-center rounded-[9px] text-muted-foreground hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 transition-colors shrink-0"
          aria-label="Log out"
          title="Log out"
        >
          <LogOut className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

const AppSidebar = ({ allowed, favourites = [], user, activeKey = 'home', onLaunch, onNav, onLogout, groupOrder, toolOrder }) => {
  const orderedGroups = useMemo(() => buildOrderedGroups(allowed, groupOrder, toolOrder), [allowed, groupOrder, toolOrder]);
  const favTools = (favourites || []).map((k) => TOOL_BY_KEY[k]).filter(Boolean);

  // Groups start collapsed; the user's expand/collapse choices persist across visits.
  const [open, setOpen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || '[]')); } catch { return new Set(); }
  });
  useEffect(() => { try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...open])); } catch { /* ignore */ } }, [open]);
  const toggle = (id) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Light up whichever item matches the current route (not just Home).
  const { pathname } = useLocation();
  const act = useMemo(() => {
    if (pathname === '/') return 'home';
    const base = (p) => (p || '').split('?')[0];
    const exact = Object.values(TOOL_BY_KEY).find((t) => base(t.path) === pathname);
    if (exact) return exact.key;
    const nested = Object.values(TOOL_BY_KEY)
      .filter((t) => t.path && t.path !== '/' && pathname.startsWith(base(t.path)))
      .sort((a, b) => base(b.path).length - base(a.path).length)[0];
    return nested?.key || activeKey;
  }, [pathname, activeKey]);

  // Reveal the group that holds the tool currently in use (never on Home).
  const activeCat = TOOL_BY_KEY[act]?.category;
  useEffect(() => { if (activeCat) setOpen((p) => (p.has(activeCat) ? p : new Set([...p, activeCat]))); }, [activeCat]);

  return (
    <aside
      data-testid="app-sidebar"
      className="w-[260px] shrink-0 h-full flex flex-col bg-card/70 backdrop-blur-xl border-r border-border/70"
    >
      <div className="h-14 px-5 flex items-center border-b border-border/50">
        <img src="/derwent-ai-logo.png" alt="DerwentAI" className="h-[22px] w-auto object-contain select-none" draggable={false} />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-none px-3 pt-3 pb-3">
        <div className="space-y-0.5">
          <Item icon={Home} color={NAV_COLOR.home} label="Home" active={act === 'home'} onClick={() => onNav('/')} testid="nav-home" />
          <Item icon={Sparkles} color={NAV_COLOR.myweek} label="MyWeek" active={['intelligence', 'hey-derwent', 'myweek'].includes(act)} onClick={() => onNav('/myweek')} testid="nav-intelligence" />
          {(user?.is_master || user?.role === 'admin') && (
            <Item icon={Activity} color={NAV_COLOR['system-diagnostics']} label="System Diagnostics" active={act === 'system-diagnostics'} onClick={() => onNav('/system-diagnostics')} testid="nav-system-diagnostics" />
          )}
          {user?.role === 'broker_support' && (
            <Item icon={Clock} color={NAV_COLOR['my-activity']} label="My Activity" active={act === 'my-activity'} onClick={() => onNav('/my-activity')} testid="nav-my-activity" />
          )}
        </div>

        {favTools.length > 0 && (
          <>
            <SectionHeader>Favourites</SectionHeader>
            <div className="space-y-0.5">
              {favTools.map((t) => (
                <Item key={t.key} icon={t.icon} color={catColor(t.key)} label={t.title} active={act === t.key} onClick={() => onLaunch(t)} testid={`sidebar-fav-${t.key}`} />
              ))}
            </div>
          </>
        )}

        <SectionHeader>Workspace</SectionHeader>
        <div className="space-y-0.5">
          {orderedGroups.map(({ cat, items }) => (
            <Group key={cat.id} cat={cat} items={items} open={open.has(cat.id)} onToggle={() => toggle(cat.id)} activeKey={act} onLaunch={onLaunch} />
          ))}
        </div>
      </div>

      <div className="px-3 pb-1">
        <SystemHealthBadge onClick={() => onNav('/system-diagnostics')} />
      </div>
      <UserBlock user={user} onLogout={onLogout} />
    </aside>
  );
};

export default AppSidebar;
